// @flow

import * as React from 'react';

/** type for the 2nd param, info, passed to componentDidCatch by React */
// This cannot be exact because it is a React type and componentDidCatch takes an inexact type
// see: https://github.com/facebook/flow/issues/5139
// eslint-disable-next-line flowtype/require-exact-type
type Info = {
  /** string stack trace provided by React */
  componentStack: string,
};
type Props = {|
  /** function that returns a react node that will be rendered if an error is caught when rendering children */
  errorRenderer?: (error: Error, info: Info) => React.Node,
  /** Callback invoked when an error is caught. Used if you want to have additional side-effects on error */
  onError?: (error: Error, info: Info) => mixed,
  /** children to render. If no error is caught these are rendered normally */
  children: React.Node,
  /** If we should record this error back to our error reporting infrastructure. Defaults to true. */
  shouldRecord?: boolean,
|};

type State = {|
  /** whether or not an exception has been caught */
  caughtError: {|
    /** Error object from caught exception */
    error: Error,
    /** Info object from caught exception */
    info: Info,
  |} | null,
|};

const errorStyle = {
  background: '#fff8f7',
  border: '1px solid #d04437',
  borderRadius: '5px',
  margin: '10px',
  padding: '10px',
  fontFamily: 'monospace',
};

/**
 * Error Boundary component -- wrap other components in an ErrorBoundary component
 * to prevent uncaught exceptions in child components from wrecking react page-wide.
 * Optionally pass in an errorRenderer function to render a replacement node
 * in case of error. If no error is caught, the children are rendered normally.
 * See https://reactjs.org/blog/2017/07/26/error-handling-in-react-16.html for more information
 *
 * In DEBUG mode this component will also output the error to the screen. This will NOT happen in production.
 */
class ErrorBoundary extends React.Component<Props, State> {
  static defaultProps = {
    errorRenderer: undefined,
    shouldRecord: true,
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      caughtError: null,
    };
  }

  componentDidCatch(error: Error, info: Info) {
    try {
      this.setState({
        caughtError: {
          error,
          info,
        },
      });
      this.logError(error);
    } catch (e) {
      this.logError(e, 'ErrorBoundaryCatchFail');
    } finally {
      if (this.props.onError) {
        this.props.onError(error, info);
      }
    }
  }

  logError = (error: Error, logger: string = 'ErrorBoundary') => {
    if (!this.props.shouldRecord) {
      return;
    }

    // FIXME: call Sentry directly and provide additional information such as tags and page context
    console.error(`[${logger}]`, error);
  };

  render() {
    try {
      const { errorRenderer } = this.props;
      const { caughtError } = this.state;

      if (caughtError) {
        const { error, info } = caughtError;
        // if we caught an error in debug mode then write out the stack trace in addition to any 'errorRenderer' content
        // provded to this ErrorBoundary
        if (DEBUG) {
          return (
            <React.Fragment>
              <div style={errorStyle}>
                {error && <pre>{error.message}</pre>}
                {info && info.componentStack && <pre>{info.componentStack.trim()}</pre>}
                {error && <pre>{error.stack}</pre>}
              </div>
              {errorRenderer && errorRenderer(error, info)}
            </React.Fragment>
          );

          // else just write out the 'errorRender' content
        } else if (errorRenderer) {
          return errorRenderer(error, info);
        }
        return null;
      }

      // if there is no error then just render the normal children
      return this.props.children;
    } catch (e) {
      this.logError(e, 'ErrorBoundaryRenderFail');
      if (DEBUG && e) {
        return (
          <div style={errorStyle}>
            <div>{e.toString()}</div>
          </div>
        );
      }
      return null;
    }
  }
}

export default ErrorBoundary;
